import * as React from 'react';
import './order-detail.scss';
import { Modal } from 'components/modal/modal';
import { digitSperator } from 'core/utils/decimalExtentions';
import { Badge } from 'components/badge/badge';
import { Order } from '../../../../../core/domain/orders-transactions/order';
import moment from 'jalali-moment';
import { Spin } from 'antd';
import { getOrderDetailByTrackingCode } from '../../../../../core/repositories/orders-transactions';
import { copyToClipboard } from '../../../../../core/utils/copyToClipboard';
import { CheckCircleTwoTone, CloseCircleTwoTone, CopyOutlined } from '@ant-design/icons';
import Countdown from 'antd/lib/statistic/Countdown';
import { Link } from 'react-router-dom';
import { getOrderTransactionStateBadgeType } from '../script';

interface Props {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    trackingId: string;
    selectedRow: Order;
    title?: string;
}

interface TrackOrderTransactionResult {
    sourceSymbol: string;
    fullName: string;
    destinationSymbol: string;
    deposit: Transaction;
    withdrawal: Transaction;
    orderTransactionState: Enum;
    orderTransactionType: Enum;
    sourceAmount: number;
    destinationAmount: number;
    orderTransactionTrackingCode: string;
    createdOn: string;
    onPrice: number;
    hasApprovedSelfie: boolean;
}

interface Transaction {
    orderTransactionTrackingCode: string;
    amount: number;
    isAutoGenerated: boolean;
    txId: string | null;
    symbol: string;
    trackingCode: string;
    depositIrtType: Enum | null;
    voucher: Voucher | null;
    network: NetworkDetails | null;
    receptMedia: MediaFile | null;
    transactionState: Enum;
    transactionType: Enum;
    wallet: WalletDetails | null;
    bankAccount: BankAccount | null;
    paidAt: string | null;
    digitalReceipt: string | null;
    gateWayName: string | null;
}

interface Voucher {
    batchNumber?: string;
    voucherCode?: string;
    voucherActivationCode?: string;
}

interface NetworkDetails {
    name?: string;
    fee?: number;
    network?: string;
}

interface MediaFile {
    id: string;
    name: string;
    contentType: string;
    fileName: string;
}

interface WalletDetails {
    walletAddress?: string;
    memo?: string;
}

interface BankAccount {
    id?: string;
    cardNumber?: string;
    iBan?: string;
    bankName?: string;
}

type Enum = {
    id: number;
    title: string;
    icon?: any;
};

export const OrderDetailModal: React.FC<Props> = ({ isOpen, setIsOpen, trackingId, title }) => {
    const [timer, setTimer] = React.useState<number>(0);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [order, setOrder] = React.useState<TrackOrderTransactionResult>();

    React.useEffect(() => {
        if (!isOpen) return;

        let active = true;
        let refreshTimer: ReturnType<typeof setTimeout> | undefined;
        let refreshCount = 0;
        setOrder(undefined);
        setTimer(0);

        const fetchDetails = async (background = false) => {
            let needsHash = false;
            try {
                await getOrderDetailByTrackingCode(
                    (value) => { if (active && !background) setLoading(value); },
                    (data: TrackOrderTransactionResult | undefined) => {
                        if (!active) return;
                        setOrder(data);
                        needsHash = data?.withdrawal?.gateWayName === 'Kucoin' &&
                            data.withdrawal.transactionState?.id === 2 && !data.withdrawal.txId;
                    },
                    (value) => { if (active) setTimer(value); },
                    trackingId,
                );
            } catch {
                // Keep the details already displayed if a background refresh fails.
            } finally {
                if (active && !background) setLoading(false);
            }

            // Sequential, bounded refreshes avoid requiring the admin to reopen the modal.
            if (active && needsHash && refreshCount < 6) {
                refreshTimer = setTimeout(() => {
                    refreshCount++;
                    void fetchDetails(true);
                }, 10000);
            }
        };

        void fetchDetails();
        return () => {
            active = false;
            clearTimeout(refreshTimer);
        };
    }, [isOpen, trackingId]);

    const safeCopy = (value?: string, label?: string) => {
        if (value) copyToClipboard(value, label ?? '');
    };

    return (
        <Modal className="pmx-order-detail" isOpen={isOpen} setIsOpen={setIsOpen}>
            {loading ? (
                <Spin spinning />
            ) : (
                order && (
                    <>
                        <h3>{title ?? 'جزئیات سفارش'}</h3>

                        {/* کد پیگیری */}
                        {order.orderTransactionTrackingCode && (
                            <div className="row">
                                <span>کد پیگیری</span>
                                <p className="en">
                                    {order.orderTransactionTrackingCode}
                                    <CopyOutlined
                                        onClick={() => safeCopy(order.orderTransactionTrackingCode, 'کد پیگیری')}
                                    />
                                </p>
                            </div>
                        )}

                        {/* کد پیگیری واریز */}
                        {order.deposit?.trackingCode && (
                            <div className="row">
                                <span>کد پیگیری واریز</span>
                                <p className="en">
                                    {order.deposit.trackingCode}
                                    <CopyOutlined
                                        onClick={() => safeCopy(order.deposit.trackingCode, 'کد پیگیری واریز')}
                                    />
                                </p>
                            </div>
                        )}

                        {/* کد پیگیری برداشت */}
                        {order.withdrawal?.trackingCode && (
                            <div className="row">
                                <span>کد پیگیری برداشت</span>
                                <p className="en">
                                    {order.withdrawal.trackingCode}
                                    <CopyOutlined
                                        onClick={() => safeCopy(order.withdrawal.trackingCode, 'کد پیگیری برداشت')}
                                    />
                                </p>
                            </div>
                        )}

                        {/* ارز مبدا و مقصد */}
                        <div className="row">
                            <span>ارز مبدا</span>
                            <p className="en">{order.sourceSymbol}</p>
                        </div>
                        <div className="row">
                            <span>ارز مقصد</span>
                            <p className="en">{order.destinationSymbol}</p>
                        </div>

                        {/* مقادیر */}
                        <div className="row">
                            <span>مقدار واریزی</span>
                            <p>
                                {order.sourceSymbol === 'IRT' ? digitSperator(order.sourceAmount) : order.sourceAmount}
                            </p>
                        </div>
                        <div className="row">
                            <span>مقدار دریافتی</span>
                            <p>
                                {order.destinationSymbol === 'IRT'
                                    ? digitSperator(order.destinationAmount)
                                    : order.destinationAmount}
                            </p>
                        </div>

                        {/* ووچر */}
                        {order.withdrawal?.gateWayName || order.deposit?.gateWayName ? (
                            <div className="row">
                                <span>نام درگاه</span>
                                <p className="en">{order.withdrawal?.gateWayName || order.deposit?.gateWayName}</p>
                            </div>
                        ) : null}

                        {/* ووچر */}
                        {order.withdrawal?.voucher?.voucherCode || order.deposit?.voucher?.voucherCode ? (
                            <div className="row">
                                <span>کد ووچر</span>
                                <p className="en">
                                    {order.withdrawal?.voucher?.voucherCode || order.deposit?.voucher?.voucherCode}
                                    <CopyOutlined
                                        onClick={() =>
                                            safeCopy(
                                                order.withdrawal?.voucher?.voucherCode ||
                                                order.deposit?.voucher?.voucherCode,
                                                'کد ووچر',
                                            )
                                        }
                                    />
                                </p>
                            </div>
                        ) : null}

                        {/* کد فعالساز */}
                        {order.withdrawal?.voucher?.voucherActivationCode ||
                            order.deposit?.voucher?.voucherActivationCode ? (
                            <div className="row">
                                <span>کد فعالساز ووچر</span>
                                <p>
                                    {order.withdrawal?.voucher?.voucherActivationCode ||
                                        order.deposit?.voucher?.voucherActivationCode}
                                </p>
                            </div>
                        ) : null}

                        {/* بچ نامبر */}
                        {order.withdrawal?.voucher?.batchNumber || order.deposit?.voucher?.batchNumber ? (
                            <div className="row">
                                <span>بچ نامبر</span>
                                <p className="en long-text">
                                    <CopyOutlined
                                        onClick={() =>
                                            safeCopy(
                                                order.withdrawal?.voucher?.batchNumber ||
                                                order.deposit?.voucher?.batchNumber,
                                                'بچ نامبر',
                                            )
                                        }
                                    />
                                    {order.withdrawal?.voucher?.batchNumber || order.deposit?.voucher?.batchNumber}
                                </p>
                            </div>
                        ) : null}

                        {/* رسید دیجیتالی */}
                        {order.withdrawal?.digitalReceipt && (
                            <div className="row copy text-overflow">
                                <span>رسید دیجیتالی</span>
                                <p className='long-text'>
                                    <CopyOutlined
                                        onClick={() => safeCopy(order.withdrawal.digitalReceipt, 'رسید دیجیتالی')}
                                    />
                                    <Link to={order.withdrawal.digitalReceipt} target='_blank'>{order.withdrawal.digitalReceipt}</Link>
                                </p>
                            </div>
                        )}

                        {/* شبا */}
                        {order.withdrawal?.bankAccount?.iBan && (
                            <div className="row copy text-overflow">
                                <span>شبا</span>
                                <p>
                                    {order.withdrawal.bankAccount.iBan}
                                    <CopyOutlined onClick={() => safeCopy(order.withdrawal.bankAccount.iBan, 'شبا')} />
                                </p>
                            </div>
                        )}

                        {/* کیف پول */}
                        {order.withdrawal?.wallet?.walletAddress && (
                            <div className="row">
                                <span>آدرس کیف پول</span>
                                <p className="en long-text">
                                    <CopyOutlined
                                        onClick={() => safeCopy(order.withdrawal.wallet.walletAddress, 'آدرس کیف پول')}
                                    />
                                    {order.withdrawal.wallet.walletAddress}
                                </p>
                            </div>
                        )}

                        {/* شبکه */}
                        {(order.withdrawal?.network?.name || order.deposit?.network?.name) && (
                            <div className="row">
                                <span>شبکه</span>
                                <p className="en">{order.withdrawal?.network?.name ?? order.deposit?.network?.name}</p>
                            </div>
                        )}

                        {/* هش */}
                        {order.withdrawal?.gateWayName === 'Kucoin' &&
                            order.withdrawal.transactionState?.id === 2 && !order.withdrawal.txId && (
                            <div className="row">
                                <span>هش برداشت</span>
                                <p>هنوز دریافت نشده است</p>
                            </div>
                        )}
                        {(order.withdrawal?.txId || order.deposit?.txId) && (
                            <div className="row">
                                <span>هش</span>
                                <p className="en long-text">
                                    <CopyOutlined
                                        onClick={() => safeCopy(order.withdrawal?.txId ?? order.deposit?.txId, 'هش')}
                                    />
                                    {order.withdrawal?.txId ?? order.deposit?.txId}
                                </p>
                            </div>
                        )}

                        {/* نام کامل */}
                        {order.fullName && (
                            <div className="row">
                                <span>نام کامل کاربر</span>
                                <p>{order.fullName}{order.hasApprovedSelfie ?
                                    <CheckCircleTwoTone twoToneColor="#52c41a" /> :
                                    <CloseCircleTwoTone twoToneColor={"red"} />}
                                </p>
                            </div>
                        )}

                        {/* نوع سفارش */}
                        <div className="row">
                            <span>نوع سفارش</span>
                            <p>
                                {order.sourceSymbol === 'IRT'
                                    ? 'خرید'
                                    : order.destinationSymbol === 'IRT'
                                        ? 'فروش'
                                        : 'تبدیل'}
                            </p>
                        </div>

                        {/* تاریخ */}
                        <div className="row">
                            <span>تاریخ و زمان سفارش</span>
                            <p>
                                {moment.utc(order.createdOn).local().locale('fa').format('YYYY/MM/DD')} |{' '}
                                {moment.utc(order.createdOn).local().locale('fa').format('HH:mm')}
                            </p>
                        </div>

                        {/* شمارش معکوس */}
                        {(order.orderTransactionState?.id === 4 || order.orderTransactionState?.id === 6) && (
                            <div className="row">
                                <span>زمان باقی مانده تا واریز</span>
                                <p>
                                    <Countdown value={timer} />
                                </p>
                            </div>
                        )}

                        {/* وضعیت */}
                        <div className="row">
                            <span>وضعیت</span>
                            <p>
                                <Badge text={order.orderTransactionState.title} type={getOrderTransactionStateBadgeType(order?.orderTransactionState?.id)} />
                            </p>
                        </div>
                    </>
                )
            )}
        </Modal>
    );
};
